import { Link } from "react-router-dom";
import type { ClubFull, HistoryItem } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";

interface IntroSectionProps {
  club: ClubFull | null;
  loading: boolean;
}

function StatItem({
  label,
  value,
  suffix,
  highlight = false,
}: {
  label: string;
  value: string;
  suffix: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 md:p-5 text-center ${
        highlight
          ? "border-secondary-400 bg-secondary-100"
          : "border-background-200 bg-background-50"
      }`}
    >
      <div
        className={`font-heading text-2xl md:text-3xl font-bold ${
          highlight ? "text-secondary-900" : "text-primary-700"
        }`}
      >
        {value}
        <span className="ml-0.5 text-sm md:text-base font-semibold">{suffix}</span>
      </div>
      <div
        className={`mt-1 text-xs md:text-sm ${
          highlight ? "text-secondary-800" : "text-foreground-500"
        }`}
      >
        {label}
      </div>
    </div>
  );
}

function Timeline({ items }: { items: HistoryItem[] }) {
  return (
    <ol className="space-y-5">
      {items.map((item, i) => {
        const prevYear = i > 0 ? items[i - 1].year : undefined;
        const showYear = item.year && item.year !== prevYear;
        return (
          <li key={`${item.year}-${i}`} className={`flex gap-4 ${showYear ? "mt-2" : ""}`}>
            <div className="w-20 md:w-24 shrink-0 pt-0.5 text-right">
              <span className="font-heading text-sm md:text-base font-bold text-primary-700 whitespace-nowrap tabular-nums">
                {showYear ? item.year : ""}
              </span>
            </div>
            <div className="relative flex-1 border-l border-background-200 pl-5 pb-1">
              <span
                className="absolute -left-[6px] top-1.5 w-3 h-3 rounded-full border-2 border-background-100 bg-secondary-500"
                aria-hidden="true"
              ></span>
              {item.date && (
                <span className="text-xs font-semibold text-secondary-800">{item.date}</span>
              )}
              <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.content}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function IntroSection({ club, loading }: IntroSectionProps) {
  const intro = club?.intro || "";
  const history = club?.history ?? [];
  const activities = club?.activities ?? [];
  const stats = club?.stats;

  const hasStats =
    !!stats && (stats.member != null || stats.photo != null || stats.years != null);

  return (
    <section className="w-full bg-background-100 px-4 md:px-6 py-14 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between gap-4 mb-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            클럽 소개
          </h2>
          <Link
            to="/about"
            className="whitespace-nowrap inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
          >
            자세히 보기
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-8">
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
            </div>
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {intro && (
              <p className="max-w-3xl whitespace-pre-line break-words text-sm md:text-base leading-relaxed text-foreground-600">
                {intro}
              </p>
            )}

            <div className={intro ? "mt-10" : ""}>
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
                주요 연혁
              </h3>
              {history.length > 0 ? (
                <Timeline items={history} />
              ) : (
                <div className="rounded-xl border border-dashed border-background-300 bg-background-50 py-12 text-center">
                  <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-300">
                    <i className="ri-history-line text-2xl" aria-hidden="true"></i>
                  </span>
                  <p className="text-sm text-foreground-500">
                    아직 등록된 연혁이 없습니다
                  </p>
                </div>
              )}
            </div>

            {activities.length > 0 && (
              <div className="mt-10">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
                  지속 활동
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activities.map((a, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                    >
                      <span className="mt-0.5 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-900">
                        <i className="ri-check-line text-sm" aria-hidden="true"></i>
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {hasStats && stats && (
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                <StatItem
                  label="재적회원"
                  value={String(stats.member ?? 0)}
                  suffix="명"
                />
                <StatItem
                  label="활동사진"
                  value={String(stats.photo ?? 0)}
                  suffix="장"
                />
                <StatItem
                  label="창립주년"
                  value={String(stats.years ?? 0)}
                  suffix="주년"
                  highlight
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}